import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { daDate, toDate, toISO, addDays, diffDays, isoWeek, monthLabel, quarterLabel, startOfMonday } from "@/lib/date";
import { MovedBadge } from "@/components/MovedBadge";
import { DanishDatePicker } from "@/components/DanishDatePicker";
import { ResponsibleMultiSelect } from "@/components/ResponsibleMultiSelect";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CalendarRange, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw,
  ChevronDown, AlertTriangle, Layers, ExternalLink, Pencil, Loader2, SlidersHorizontal,
  Building2, Clock,
} from "lucide-react";

const TASK_STATUSES = ["Ikke startet", "I gang", "Afventer", "Færdig"];
const SCALES = [["dag", "Dag"], ["uge", "Uge"], ["måned", "Måned"], ["kvartal", "Kvartal"]];
const PX = { dag: 42, uge: 20, "måned": 7, kvartal: 3.4 };
const BAR = { "Ikke startet": "bg-stone-300", "I gang": "bg-[#A08979]", "Afventer": "bg-[#C9A83F]", "Færdig": "bg-[#7E856C]" };
const DOT = { "Ikke startet": "bg-stone-400", "I gang": "bg-[#A08979]", "Afventer": "bg-[#C9A83F]", "Færdig": "bg-[#7E856C]", "Forsinket": "bg-[#A83232]" };
const STATUS_BADGE = {
  "Ikke startet": "bg-stone-50 text-stone-500 border-stone-200",
  "I gang": "bg-[#F4EFEC] text-[#806B61] border-[#E6DDD6]",
  "Afventer": "bg-[#F9F1D9] text-[#8A6E17] border-[#ECE0B6]",
  "Færdig": "bg-[#E7EADF] text-[#7E856C] border-[#D3D8C6]",
};
const SOURCE_BADGE = {
  "Salg": "bg-[#F9F1D9] text-[#8A6E17] border-[#ECE0B6]",
  "Kvalitet": "bg-[#E7EADF] text-[#7E856C] border-[#D3D8C6]",
  "Indkøb": "bg-[#F4F0EA] text-[#75654F] border-[#E4DBCF]",
};
const LEFT = 320;
const LOAD_CHIP = {
  "Ingen belastning": "bg-stone-50 text-stone-500 border-stone-200",
  "Normal": "bg-[#E7EADF] text-[#7E856C] border-[#D3D8C6]",
  "Høj belastning": "bg-[#F4EFEC] text-[#806B61] border-[#E6DDD6]",
  "Overbelastet": "bg-[#F1DEDA] text-[#A83232] border-[#E9C9C3]",
};
const LOAD_BAR = { "Ingen belastning": "bg-stone-300", "Normal": "bg-[#7E856C]", "Høj belastning": "bg-[#A08979]", "Overbelastet": "bg-[#A83232]" };
const OVERLOAD_WEEK = 5;

function buildTicks(from, to, scale, ppd) {
  const start = toDate(from), end = toDate(to);
  if (!start || !end) return [];
  const ticks = [];
  const push = (d, w, label) => ticks.push({ left: diffDays(d, start) * ppd, w: w * ppd, label });
  if (scale === "dag") {
    let d = new Date(start);
    while (d <= end) { push(d, 1, `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}`); d = addDays(d, 1); }
  } else if (scale === "uge") {
    let d = startOfMonday(start);
    while (d <= end) { push(d, 7, `Uge ${isoWeek(d)}`); d = addDays(d, 7); }
  } else if (scale === "måned") {
    let d = new Date(start.getFullYear(), start.getMonth(), 1);
    while (d <= end) { const n = new Date(d.getFullYear(), d.getMonth() + 1, 1); push(d, Math.round((n - d) / 86400000), monthLabel(d)); d = n; }
  } else {
    let d = new Date(start.getFullYear(), Math.floor(start.getMonth() / 3) * 3, 1);
    while (d <= end) { const n = new Date(d.getFullYear(), d.getMonth() + 3, 1); push(d, Math.round((n - d) / 86400000), quarterLabel(d)); d = n; }
  }
  return ticks;
}
function hasOverlap(task, siblings) {
  if (!task.has_period) return false;
  const s = toDate(task.start_date), e = toDate(task.expected_end_date);
  return siblings.some((o) => o.id !== task.id && o.has_period && toDate(o.start_date) <= e && s <= toDate(o.expected_end_date));
}
const respIds = (t) => (t.responsible_user_ids && t.responsible_user_ids.length ? t.responsible_user_ids : (t.responsible_user_id ? [t.responsible_user_id] : []));

export default function TotalPlan() {
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scale, setScale] = useState("uge");
  const [zoom, setZoom] = useState(1);
  const [collapsed, setCollapsed] = useState({});
  const [sortBy, setSortBy] = useState("load");
  const [editing, setEditing] = useState(null);
  const [openLoad, setOpenLoad] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [warnOpen, setWarnOpen] = useState(false);
  const scrollRef = useRef(null);

  const today = toISO(new Date());
  const defFrom = toISO(addDays(new Date(), -30));
  const defTo = toISO(addDays(new Date(), 180));
  const [f, setF] = useState({
    employee: "all", source: "all", customer: "all", taskStatus: "all",
    from: defFrom, to: defTo, onlyOpen: false, onlyDelayed: false, showCompleted: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tp, us] = await Promise.all([api.get("/total-plan"), api.get("/users").catch(() => ({ data: [] }))]);
      setData(tp.data);
      setUsers(us ? us.data.filter((u) => u.active !== false) : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kunne ikke hente total planlægning");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const allTasks = data?.tasks || [];
  const assignees = data?.assignees || [];
  const loadWarnings = data?.load_warnings || [];
  const loadSummary = data?.load_summary || null;
  const loadCurrent = data?.load_current || {};
  const weekGroups = useMemo(() => {
    const m = new Map();
    for (const w of loadWarnings) {
      const key = `${w.year}-${w.week}`;
      if (!m.has(key)) m.set(key, { key, year: w.year, week: w.week, week_start: w.week_start, items: [] });
      m.get(key).items.push(w);
    }
    return [...m.values()];
  }, [loadWarnings]);
  const nowYear = new Date().getFullYear();

  const sourceOpts = useMemo(() => [...new Set(allTasks.map((t) => t.source_label).filter(Boolean))], [allTasks]);
  const customerOpts = useMemo(() => [...new Set(allTasks.map((t) => t.customer_name).filter(Boolean))], [allTasks]);

  const filtered = useMemo(() => allTasks.filter((t) => {
    if (f.employee !== "all" && !respIds(t).includes(f.employee)) return false;
    if (f.source !== "all" && t.source_label !== f.source) return false;
    if (f.customer !== "all" && t.customer_name !== f.customer) return false;
    if (f.taskStatus !== "all" && t.status !== f.taskStatus) return false;
    if (!f.showCompleted && t.status === "Færdig") return false;
    if (f.onlyOpen && t.status === "Færdig") return false;
    if (f.onlyDelayed && !t.is_delayed) return false;
    return true;
  }), [allTasks, f]);

  const withPeriod = filtered.filter((t) => t.has_period && toDate(t.expected_end_date) >= toDate(f.from) && toDate(t.start_date) <= toDate(f.to));

  const groups = useMemo(() => {
    const nameMap = new Map(assignees.map((a) => [a.id, a.name]));
    const map = new Map();
    const addTo = (key, name, t) => {
      if (!map.has(key)) map.set(key, { key, name, tasks: [] });
      map.get(key).tasks.push(t);
    };
    for (const t of withPeriod) {
      const rids = respIds(t);
      if (f.employee !== "all") {
        if (rids.includes(f.employee)) addTo(f.employee, nameMap.get(f.employee) || t.responsible_user_name || "—", t);
        continue;
      }
      if (!rids.length) { addTo("__none__", "Ingen ansvarlig", t); continue; }
      for (const rid of rids) addTo(rid, nameMap.get(rid) || t.responsible_user_name || "—", t);
    }
    const arr = [...map.values()];
    for (const g of arr) {
      g.tasks.sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
      g.active = g.tasks.filter((t) => t.status !== "Færdig").length;
      g.delayed = g.tasks.filter((t) => t.is_delayed).length;
      const lc = loadCurrent[g.key];
      const lbl = lc ? lc.label : "Ingen belastning";
      g.load = { label: lbl, cls: LOAD_CHIP[lbl] };
      g.weekLoad = lc ? lc.count : 0;
      const pmap = new Map();
      for (const t of g.tasks) {
        if (!pmap.has(t.group_id)) pmap.set(t.group_id, { group_id: t.group_id, group_title: t.group_title, source_label: t.source_label, deadline_history: t.deadline_history || [], tasks: [] });
        pmap.get(t.group_id).tasks.push(t);
      }
      g.groups = [...pmap.values()];
      for (const p of g.groups) {
        p.tasks.sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
        p.start = p.tasks.reduce((m, t) => (t.start_date && (!m || t.start_date < m) ? t.start_date : m), null);
        p.end = p.tasks.reduce((m, t) => (t.expected_end_date && (!m || t.expected_end_date > m) ? t.expected_end_date : m), null);
        p.anyDelayed = p.tasks.some((t) => t.is_delayed);
      }
      g.groups.sort((a, b) => (a.start || "").localeCompare(b.start || ""));
    }
    const loadRank = { "Ingen belastning": 0, Normal: 1, "Høj belastning": 2, Overbelastet: 3 };
    arr.sort((a, b) => {
      if (sortBy === "active") return b.active - a.active;
      if (sortBy === "delayed") return b.delayed - a.delayed;
      if (sortBy === "load") return loadRank[b.load.label] - loadRank[a.load.label] || b.active - a.active;
      return a.name.localeCompare(b.name, "da");
    });
    return arr;
  }, [withPeriod, sortBy, assignees, f.employee, loadCurrent]);

  const unscheduledTasks = useMemo(() => filtered.filter((t) => !t.has_period), [filtered]);

  const loadOverview = useMemo(() => {
    const m = new Map(assignees.map((a) => [a.id, { id: a.id, name: a.name, tasks: [] }]));
    for (const t of allTasks) {
      if (t.status === "Færdig") continue;
      for (const rid of respIds(t)) {
        if (!rid) continue;
        if (!m.has(rid)) m.set(rid, { id: rid, name: t.responsible_user_name || "—", tasks: [] });
        m.get(rid).tasks.push(t);
      }
    }
    const rank = { "Overbelastet": 3, "Høj belastning": 2, "Normal": 1, "Ingen belastning": 0 };
    const arr = [...m.values()].map((u) => {
      const lc = loadCurrent[u.id];
      const week = lc ? lc.count : 0;
      const label = lc ? lc.label : "Ingen belastning";
      return { ...u, week, total: u.tasks.length, label };
    });
    arr.sort((a, b) => rank[b.label] - rank[a.label] || b.week - a.week || b.total - a.total || a.name.localeCompare(b.name, "da"));
    return arr;
  }, [allTasks, assignees, loadCurrent]);

  const capacityWarnings = useMemo(() => {
    const nameMap = new Map(assignees.map((a) => [a.id, a.name]));
    const perEmp = new Map();
    for (const t of allTasks) {
      if (t.status === "Færdig" || !t.has_period) continue;
      const s = toDate(t.start_date), e = toDate(t.expected_end_date);
      if (!s || !e) continue;
      for (const rid of respIds(t)) {
        if (!rid) continue;
        if (!perEmp.has(rid)) perEmp.set(rid, new Map());
        const wm = perEmp.get(rid);
        let wk = startOfMonday(s);
        while (wk <= e) {
          const wkEnd = addDays(wk, 6);
          if (s <= wkEnd && e >= wk) {
            const key = toISO(wk);
            if (!wm.has(key)) wm.set(key, { count: 0 });
            wm.get(key).count++;
          }
          wk = addDays(wk, 7);
        }
      }
    }
    const out = [];
    for (const [rid, wm] of perEmp) {
      const weeks = [...wm.entries()].filter(([, v]) => v.count >= OVERLOAD_WEEK)
        .map(([k, v]) => ({ weekStart: k, week: isoWeek(toDate(k)), count: v.count }));
      if (weeks.length) {
        weeks.sort((a, b) => a.weekStart.localeCompare(b.weekStart));
        out.push({ id: rid, name: nameMap.get(rid) || "—", peak: Math.max(...weeks.map((w) => w.count)), weeks });
      }
    }
    out.sort((a, b) => b.peak - a.peak || a.name.localeCompare(b.name, "da"));
    return out;
  }, [allTasks, assignees]);

  const focusWeek = (empId, weekStartISO) => {
    setF((p) => ({ ...p, employee: empId, from: toISO(addDays(toDate(weekStartISO), -7)), to: toISO(addDays(toDate(weekStartISO), 35)) }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };


  const ppd = PX[scale] * zoom;
  const ticks = useMemo(() => buildTicks(f.from, f.to, scale, ppd), [f.from, f.to, scale, ppd]);
  const totalW = Math.max(diffDays(toDate(f.to), toDate(f.from)) * ppd, 200);
  const todayLeft = diffDays(toDate(today), toDate(f.from)) * ppd;
  const scrolledToToday = useRef(false);
  useEffect(() => {
    if (scrolledToToday.current || loading || !rangeInit.current || !scrollRef.current) return;
    scrolledToToday.current = true;
    scrollRef.current.scrollLeft = Math.max(0, todayLeft - ppd);
  }, [todayLeft, loading, ppd]);
  const todayVisible = today >= f.from && today <= f.to;

  const shiftPeriod = (dir) => {
    const len = diffDays(toDate(f.to), toDate(f.from));
    setF((p) => ({ ...p, from: toISO(addDays(toDate(p.from), dir * len)), to: toISO(addDays(toDate(p.to), dir * len)) }));
  };
  const centerToday = () => {
    const len = diffDays(toDate(f.to), toDate(f.from));
    setF((p) => ({ ...p, from: toISO(addDays(new Date(), -Math.round(len / 2))), to: toISO(addDays(new Date(), Math.round(len / 2))) }));
  };
  const resetFilters = () => setF({ employee: "all", source: "all", customer: "all", taskStatus: "all", from: defFrom, to: defTo, onlyOpen: false, onlyDelayed: false, showCompleted: false });

  const rangeInit = useRef(false);
  useEffect(() => {
    if (rangeInit.current) return;
    const periodTasks = allTasks.filter((t) => t.has_period && t.start_date);
    if (!periodTasks.length) return;
    rangeInit.current = true;
    let minStart = periodTasks[0].start_date;
    let maxEnd = periodTasks[0].expected_end_date || periodTasks[0].start_date;
    for (const t of periodTasks) {
      if (t.start_date < minStart) minStart = t.start_date;
      const e = t.expected_end_date || t.start_date;
      if (e > maxEnd) maxEnd = e;
    }
    const from = toISO(startOfMonday(addDays(toDate(minStart), -7)));
    const to = [toISO(addDays(toDate(maxEnd), 21)), toISO(addDays(new Date(), 90))].sort().pop();
    setF((p) => ({ ...p, from, to }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTasks]);

  if (loading) {
    return <div className="flex items-center justify-center py-32 text-stone-400" data-testid="tp-loading"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Henter total planlægning…</div>;
  }

  return (
    <div className="space-y-6" data-testid="total-plan-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-[#1A1919]">Total planlægning</h1>
          <p className="text-sm text-stone-500 mt-2 max-w-xl">Alle projekttrin og sagsopgaver fordelt på alle medarbejdere — ét samlet ledelsesoverblik.</p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-[#EBE8E3] rounded-xl p-1" data-testid="tp-sort">
          {[["load", "Belastning"], ["active", "Flest aktive"], ["delayed", "Flest forsinkede"], ["name", "Navn"]].map(([v, l]) => (
            <button key={v} data-testid={`tp-sort-${v}`} onClick={() => setSortBy(v)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${sortBy === v ? "bg-[#1A1919] text-white" : "text-stone-500 hover:bg-[#F3F1EC]"}`}>{l}</button>
          ))}
        </div>
      </div>

      {loadSummary && loadSummary.total > 0 && (
        <div className="rounded-2xl border border-[#E9C9C3] bg-[#F1DEDA]/50 p-5" data-testid="tp-capacity-warning">
          <button onClick={() => setWarnOpen((v) => !v)} className="w-full flex flex-wrap items-center gap-x-2 gap-y-1 text-left" data-testid="tp-capacity-toggle">
            <AlertTriangle className="h-4 w-4 text-[#A83232] shrink-0" strokeWidth={2} />
            <h2 className="text-sm font-semibold text-[#A83232]">Fremtidig belastningsadvarsel</h2>
            <span className="text-xs text-[#A83232]/80">
              {loadSummary.total} {loadSummary.total === 1 ? "belastningsproblem" : "belastningsproblemer"} · {loadSummary.employees_affected} {loadSummary.employees_affected === 1 ? "medarbejder" : "medarbejdere"} · første i uge {loadSummary.earliest_week}
              {loadSummary.over ? ` · ${loadSummary.over} overbelastet` : ""}{loadSummary.high ? ` · ${loadSummary.high} høj` : ""}
            </span>
            <ChevronDown className={`h-4 w-4 text-[#A83232] ml-auto shrink-0 [transition-property:transform] duration-200 ${warnOpen ? "rotate-180" : ""}`} />
          </button>
          {warnOpen && (
            <div className="mt-4 space-y-4" data-testid="tp-capacity-list">
              {weekGroups.map((g) => (
                <div key={g.key}>
                  <button onClick={() => focusWeek("all", g.week_start)} className="text-xs font-semibold text-[#8A3535] mb-1.5 hover:underline" data-testid={`tp-cap-week-${g.year}-${g.week}`}>
                    Uge {g.week}{nowYear !== g.year ? ` · ${g.year}` : ""}
                  </button>
                  <div className="space-y-1.5">
                    {g.items.map((w) => (
                      <button key={`${w.user_id}:${w.week}`} onClick={() => focusWeek(w.user_id, w.week_start)} data-testid={`tp-cap-emp-${w.user_id}-${w.year}-${w.week}`}
                        className="w-full flex items-center gap-3 rounded-xl bg-white border border-[#E9C9C3] px-3 py-2 hover:bg-[#F1DEDA] [transition-property:background-color] duration-200 text-left">
                        <span className={`h-2 w-2 rounded-full shrink-0 ${w.status === "Overbelastet" ? "bg-[#A83232]" : "bg-[#A08979]"}`} />
                        <span className="text-sm font-medium text-[#1A1919] flex-1 min-w-0 truncate">{w.name}</span>
                        <span className={`text-xs font-medium shrink-0 ${w.status === "Overbelastet" ? "text-[#A83232]" : "text-[#806B61]"}`}>{w.status}</span>
                        <span className="text-xs text-stone-500 tabular-nums w-20 text-right shrink-0">{w.count} opgaver</span>
                        <span className="text-[11px] text-stone-400 shrink-0 w-16 text-right">om {w.weeks_ahead} {w.weeks_ahead === 1 ? "uge" : "uger"}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {(() => {
        const active = [f.employee !== "all", f.source !== "all", f.customer !== "all", f.taskStatus !== "all", f.onlyOpen, f.onlyDelayed, f.showCompleted].filter(Boolean).length;
        return (
          <button type="button" data-testid="tp-toggle-filters" onClick={() => setShowFilters((v) => !v)} className="inline-flex items-center gap-2 text-sm font-medium text-stone-600 hover:text-[#1A1919] bg-white border border-[#EBE8E3] rounded-xl px-4 h-10 [transition-property:color,border-color] duration-200">
            <SlidersHorizontal className="h-4 w-4" strokeWidth={1.75} /> Filtre
            {active > 0 && <span className="bg-[#A83232] text-white text-[11px] font-semibold rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center">{active}</span>}
            <ChevronDown className={`h-4 w-4 [transition-property:transform] duration-200 ${showFilters ? "rotate-180" : ""}`} />
          </button>
        );
      })()}
      {showFilters && (
      <div className="bg-white border border-[#EBE8E3] rounded-2xl p-5 space-y-4" data-testid="tp-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <FilterSelect testid="tp-filter-employee" label="Medarbejder" value={f.employee} onChange={(v) => setF({ ...f, employee: v })}
            options={[["all", "Alle medarbejdere"], ...assignees.map((a) => [a.id, a.name])]} />
          <FilterSelect testid="tp-filter-source" label="Afdeling" value={f.source} onChange={(v) => setF({ ...f, source: v })}
            options={[["all", "Alle afdelinger"], ...sourceOpts.map((c) => [c, c])]} />
          <FilterSelect testid="tp-filter-customer" label="Kunde" value={f.customer} onChange={(v) => setF({ ...f, customer: v })}
            options={[["all", "Alle kunder"], ...customerOpts.map((c) => [c, c])]} />
          <FilterSelect testid="tp-filter-tstatus" label="Status" value={f.taskStatus} onChange={(v) => setF({ ...f, taskStatus: v })}
            options={[["all", "Alle"], ...TASK_STATUSES.map((s) => [s, s])]} />
          <div className="space-y-1.5">
            <Label className="text-xs text-stone-500">Fra dato</Label>
            <DanishDatePicker value={f.from} onChange={(v) => setF({ ...f, from: v })} testid="tp-filter-from" allowClosed />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-stone-500">Til dato</Label>
            <DanishDatePicker value={f.to} onChange={(v) => setF({ ...f, to: v })} testid="tp-filter-to" allowClosed />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-1">
          <CheckToggle testid="tp-only-open" checked={f.onlyOpen} onChange={(v) => setF({ ...f, onlyOpen: v })} label="Kun åbne opgaver" />
          <CheckToggle testid="tp-only-delayed" checked={f.onlyDelayed} onChange={(v) => setF({ ...f, onlyDelayed: v })} label="Kun forsinkede opgaver" />
          <CheckToggle testid="tp-show-completed" checked={f.showCompleted} onChange={(v) => setF({ ...f, showCompleted: v })} label="Vis afsluttede opgaver" />
          <Button variant="ghost" size="sm" className="rounded-xl text-stone-500 ml-auto" data-testid="tp-reset" onClick={resetFilters}>
            <RotateCcw className="h-4 w-4 mr-2" strokeWidth={1.75} /> Nulstil filtre
          </Button>
        </div>
      </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 bg-white border border-[#EBE8E3] rounded-xl p-1" data-testid="tp-scale">
          {SCALES.map(([v, l]) => (
            <button key={v} data-testid={`tp-scale-${v}`} onClick={() => setScale(v)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${scale === v ? "bg-[#1A1919] text-white" : "text-stone-500 hover:bg-[#F3F1EC]"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-white border border-[#EBE8E3] rounded-xl p-1">
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" data-testid="tp-prev" onClick={() => shiftPeriod(-1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" className="rounded-lg h-8" data-testid="tp-today" onClick={centerToday}>I dag</Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" data-testid="tp-next" onClick={() => shiftPeriod(1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="flex items-center gap-1 bg-white border border-[#EBE8E3] rounded-xl p-1">
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" data-testid="tp-zoom-out" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}><ZoomOut className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" data-testid="tp-zoom-in" onClick={() => setZoom((z) => Math.min(2.5, +(z + 0.25).toFixed(2)))}><ZoomIn className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 text-xs text-stone-500">
        {["Ikke startet", "I gang", "Afventer", "Færdig", "Forsinket"].map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${DOT[s]}`} /> {s}</span>
        ))}
        <span className="inline-flex items-center gap-1.5 ml-2 text-stone-400"><span className="h-3 w-px bg-[#A83232]" /> I dag</span>
      </div>

      <div className="hidden md:block border border-[#EBE8E3] rounded-2xl bg-white overflow-hidden" data-testid="tp-gantt">
        <div className="overflow-x-auto" ref={scrollRef}>
          <div style={{ width: LEFT + totalW, minWidth: "100%" }}>
            <div className="flex sticky top-0 z-20 bg-[#FBFAF8] border-b border-[#EBE8E3]">
              <div className="sticky left-0 z-30 bg-[#FBFAF8] border-r border-[#EBE8E3] shrink-0 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-stone-400" style={{ width: LEFT }}>Medarbejder / opgave</div>
              <div className="relative h-10" style={{ width: totalW }}>
                {ticks.map((t, i) => (
                  <div key={i} className="absolute top-0 h-full border-l border-[#F3F1EC] text-[11px] text-stone-500 pl-1.5 pt-2 whitespace-nowrap overflow-hidden" style={{ left: t.left, width: t.w }}>{t.label}</div>
                ))}
                {todayVisible && <div data-testid="tp-today-line-header" className="absolute top-0 h-full w-0.5 bg-[#A83232] z-10" style={{ left: todayLeft }} />}
              </div>
            </div>

            {groups.length === 0 && (
              <div className="px-4 py-16 text-center text-stone-400" data-testid="tp-empty">
                {allTasks.length === 0 ? "Ingen planlagte opgaver endnu." : "Ingen opgaver matcher de valgte filtre."}
              </div>
            )}

            {groups.map((g) => (
              <div key={g.key}>
                <div className="flex bg-[#FBFAF8]/60 border-b border-[#F3F1EC]">
                  <div className="sticky left-0 z-10 bg-[#FBFAF8] border-r border-[#EBE8E3] shrink-0 px-4 py-2.5 flex items-center gap-2" style={{ width: LEFT }}>
                    <button onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))} data-testid={`tp-group-toggle-${g.key}`} className="text-stone-400 hover:text-stone-700">
                      <ChevronDown className={`h-4 w-4 transition-transform ${collapsed[g.key] ? "-rotate-90" : ""}`} />
                    </button>
                    <span className="font-semibold text-sm text-stone-800 truncate" data-testid={`tp-group-name-${g.key}`}>{g.name}</span>
                    <span className="text-xs text-stone-400 shrink-0">· {g.active} aktive</span>
                    <Badge variant="outline" data-testid={`tp-load-${g.key}`} className={`ml-auto rounded-lg text-[11px] shrink-0 ${g.load.cls}`} title="Belastning pr. uge: Normal = 1–4 opgaver · Høj belastning = 5–8 · Overbelastet = 9 eller flere. Samme opgave tælles i hver uge, den løber over.">{g.load.label}</Badge>
                  </div>
                  <div className="relative bg-white" style={{ width: totalW }}>{todayVisible && <div className="absolute top-0 h-full w-0.5 bg-[#A83232]/40" style={{ left: todayLeft }} />}</div>
                </div>

                {!collapsed[g.key] && g.groups.map((p) => {
                  const barLeft = diffDays(toDate(p.start), toDate(f.from)) * ppd;
                  const barW = Math.max((diffDays(toDate(p.end), toDate(p.start)) + 1) * ppd, ppd);
                  return (
                    <div key={p.group_id} className="flex border-b border-[#F3F1EC] hover:bg-[#FBFAF8]/50 group" data-testid={`tp-group-row-${g.key}-${p.group_id}`}>
                      <div className="sticky left-0 z-10 bg-white group-hover:bg-[#FBFAF8] border-r border-[#EBE8E3] shrink-0 px-4 py-3 pl-9 flex items-center gap-2" style={{ width: LEFT }}>
                        <span className={`h-2 w-2 rounded-full shrink-0 ${p.anyDelayed ? DOT["Forsinket"] : "bg-stone-300"}`} />
                        <span className="text-sm font-medium text-stone-800 truncate" title={p.group_title}>{p.group_title}</span>
                        <MovedBadge history={p.deadline_history} testid={`tp-moved-${p.group_id}`} />
                        <Badge variant="outline" className={`ml-auto shrink-0 rounded-md text-[10px] px-1.5 py-0 ${SOURCE_BADGE[p.source_label] || "bg-stone-50 text-stone-500 border-stone-200"}`}>{p.source_label}</Badge>
                      </div>
                      <div className="relative py-3" style={{ width: totalW }}>
                        {ticks.map((tk, i) => <div key={i} className="absolute top-0 h-full border-l border-[#F3F1EC]" style={{ left: tk.left }} />)}
                        {todayVisible && <div className="absolute top-0 h-full w-0.5 bg-[#A83232]/40" style={{ left: todayLeft }} />}
                        <div data-testid={`tp-group-bar-${p.group_id}`} className="absolute rounded-lg bg-stone-100 ring-1 ring-stone-200/70 overflow-hidden shadow-sm" style={{ left: Math.max(barLeft, 0), width: barW, top: 8, height: 26 }}>
                          {p.tasks.map((seg) => {
                            const segLeft = diffDays(toDate(seg.start_date), toDate(p.start)) * ppd;
                            const segW = Math.max((diffDays(toDate(seg.expected_end_date), toDate(seg.start_date)) + 1) * ppd, 4);
                            return (
                              <Popover key={seg.id}>
                                <PopoverTrigger asChild>
                                  <button data-testid={`tp-segment-${seg.id}`}
                                    className={`absolute top-0 h-full ${seg.is_delayed ? "bg-[#A83232]" : BAR[seg.status]} hover:brightness-95 transition-all border-r border-white/70 last:border-r-0 flex items-center px-2`}
                                    style={{ left: Math.max(segLeft, 0), width: segW }} title={`${seg.item_name} · ${seg.status}`}>
                                    <span className="text-[10px] font-medium text-white/95 truncate">{seg.item_name}</span>
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-80 rounded-2xl" data-testid={`tp-card-${seg.id}`}>
                                  <TaskCard t={seg} overlap={hasOverlap(seg, g.tasks)} onEdit={() => setEditing(seg)} />
                                </PopoverContent>
                              </Popover>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="md:hidden space-y-3" data-testid="tp-mobile-list">
        {groups.length === 0 && <p className="text-center text-stone-400 py-10">{allTasks.length === 0 ? "Ingen planlagte opgaver endnu." : "Ingen opgaver matcher de valgte filtre."}</p>}
        {groups.map((g) => (
          <div key={g.key} className="bg-white border border-[#EBE8E3] rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="font-semibold text-sm">{g.name}</span>
              <span className="text-xs text-stone-400">· {g.active} aktive</span>
              <Badge variant="outline" className={`ml-auto rounded-lg text-[11px] ${g.load.cls}`}>{g.load.label}</Badge>
            </div>
            <div className="space-y-2">
              {g.tasks.map((t) => (
                <div key={t.id} className="rounded-xl border border-[#F3F1EC] p-3" data-testid={`tp-mobile-task-${t.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{t.group_title}</span>
                    <Badge variant="outline" className={`rounded-lg text-[11px] shrink-0 ${t.is_delayed ? "bg-[#F1DEDA] text-[#A83232] border-[#E9C9C3]" : STATUS_BADGE[t.status]}`}>{t.is_delayed ? `Forsinket ${t.delay_days}d` : t.status}</Badge>
                  </div>
                  <p className="text-xs text-stone-500 mt-1">{t.item_name} · {t.source_label}</p>
                  <p className="text-xs text-stone-400 mt-1">{daDate(t.start_date)} – {daDate(t.expected_end_date)}</p>
                  <Link to={t.link} className="text-xs text-[#A83232] font-medium inline-flex items-center gap-1 mt-2">Åbn <ExternalLink className="h-3 w-3" /></Link>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {unscheduledTasks.length > 0 && (
        <div data-testid="tp-unscheduled">
          <div className="flex items-center justify-between mb-5">
            <h2 className="label-caps">Uden tidsplan</h2>
            <span className="text-xs font-medium text-stone-400">{unscheduledTasks.length}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {unscheduledTasks.map((t) => (
              <Link key={t.id} to={t.link} data-testid={`tp-unscheduled-${t.id}`} className="group rounded-2xl border border-[#EBE8E3] bg-white p-5 hover:border-[#A83232]/50 [transition-property:border-color] duration-200">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-stone-400">{t.source_label}</span>
                  <Badge variant="outline" className={`rounded-md text-[10px] px-1.5 py-0 ${STATUS_BADGE[t.status] || ""}`}>{t.status}</Badge>
                </div>
                <p className="font-semibold text-[#1A1919] truncate mt-1 group-hover:text-[#A83232] [transition-property:color] duration-200">{t.group_title}</p>
                <p className="text-sm text-stone-500 truncate">{t.item_name}</p>
                <p className="text-xs text-stone-400 mt-3">{t.responsible_user_name || t.responsible_name || "Ingen ansvarlig"}</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div data-testid="tp-load-overview">
        <h2 className="label-caps mb-5">Belastningsoversigt · alle medarbejdere</h2>
        <div className="rounded-2xl border border-[#EBE8E3] bg-white divide-y divide-[#F3F1EC]">
          {loadOverview.map((u) => {
            const open = openLoad === u.id;
            return (
              <div key={u.id}>
                <button onClick={() => setOpenLoad(open ? null : u.id)} data-testid={`tp-load-row-${u.id}`} className="w-full text-left flex items-center gap-4 px-5 py-4 hover:bg-[#FBFAF8] transition-colors">
                  <span className="text-sm font-medium text-[#1A1919] w-48 shrink-0 truncate">{u.name}</span>
                  <div className="hidden sm:block flex-1 h-1.5 rounded-full bg-[#ECEAE5] overflow-hidden"><div className={`h-full rounded-full ${LOAD_BAR[u.label]}`} style={{ width: `${Math.min((u.week || 0) / 7 * 100, 100)}%` }} /></div>
                  <span className="text-sm text-stone-500 shrink-0 ml-auto sm:ml-0 tabular-nums">{u.week || 0} pr. uge · {u.total} i alt</span>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-lg border px-2.5 py-1 shrink-0 ${LOAD_CHIP[u.label]}`}><span className={`h-1.5 w-1.5 rounded-full ${LOAD_BAR[u.label]}`} /> {u.label}</span>
                  <ChevronDown className={`h-4 w-4 text-stone-400 shrink-0 [transition-property:transform] duration-200 ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div className="bg-[#FBFAF8] px-5 py-3 space-y-2" data-testid={`tp-load-tasks-${u.id}`}>
                    {u.tasks.length === 0 ? <p className="text-sm text-stone-400 py-2">Ingen aktive opgaver.</p> : u.tasks.map((t) => (
                      <Link key={t.id} to={t.link} data-testid={`tp-load-task-${t.id}`} className="rounded-xl border border-[#EBE8E3] bg-white hover:border-stone-300 transition-colors px-4 py-2.5 flex items-center gap-3">
                        <span className="min-w-0 flex-1"><span className="block font-medium text-[#1A1919] truncate text-sm">{t.item_name}</span><span className="block text-xs text-stone-500 truncate mt-0.5">{t.group_title} · {t.source_label}</span></span>
                        <span className={`text-xs font-medium shrink-0 ${t.is_delayed ? "text-[#A83232]" : "text-stone-500"}`}>{t.expected_end_date ? daDate(t.expected_end_date) : "Ingen dato"}</span>
                        <ChevronRight className="h-4 w-4 text-stone-300 shrink-0" />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-stone-400 mt-3">Ingen belastning = 0 · Normal 1–4 · Høj belastning 5–8 · Overbelastet 9+ opgaver pr. uge. Samme opgave tælles i hver uge, den løber over. Baseret på antal opgaver, ikke arbejdstimer.</p>
      </div>

      {editing && <EditDialog task={editing} users={users} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-stone-500">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-10 rounded-xl bg-white" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
function CheckToggle({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(Boolean(v))} data-testid={testid} />
      <span className="text-sm text-stone-600">{label}</span>
    </label>
  );
}
function InfoRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon className="h-4 w-4 text-stone-400 shrink-0" strokeWidth={1.75} />
      <span className="text-stone-500 shrink-0">{label}</span>
      <span className="font-medium text-stone-800 text-right ml-auto truncate">{children}</span>
    </div>
  );
}

function TaskCard({ t, overlap, onEdit }) {
  const responsibles = (t.responsible_user_name || "").split(",").map((s) => s.trim()).filter(Boolean);
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400 truncate">{t.source_label}{t.customer_name ? ` · ${t.customer_name}` : ""}</p>
          <p className="text-base font-semibold text-stone-900 leading-tight mt-1">{t.group_title}</p>
          <p className="text-sm text-stone-500 mt-0.5">{t.item_name}</p>
        </div>
        <Badge variant="outline" className={`rounded-full px-2.5 py-0.5 text-[11px] shrink-0 ${t.is_delayed ? "bg-[#F1DEDA] text-[#A83232] border-[#E9C9C3]" : STATUS_BADGE[t.status]}`}>{t.is_delayed ? `Forsinket ${t.delay_days}d` : t.status}</Badge>
      </div>
      <div>
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-stone-400">Fremdrift</span>
          <span className="font-semibold text-stone-700">{t.progress}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-[#F3F1EC] overflow-hidden">
          <div className={`h-full rounded-full [transition-property:width] duration-500 ${t.is_delayed ? "bg-[#A83232]" : t.progress === 100 ? "bg-[#7E856C]" : "bg-[#A83232]/70"}`} style={{ width: `${Math.max(t.progress, 3)}%` }} />
        </div>
      </div>
      <div>
        <p className="text-xs text-stone-400 mb-1.5">Ansvarlige</p>
        <div className="flex flex-wrap gap-1.5">
          {responsibles.length ? responsibles.map((r, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#EBE8E3] pl-1 pr-2.5 py-0.5 text-xs font-medium text-stone-700">
              <span className="h-5 w-5 rounded-full bg-[#A83232]/10 text-[#A83232] text-[10px] font-semibold flex items-center justify-center">{r.slice(0, 1).toUpperCase()}</span>
              {r}
            </span>
          )) : <span className="text-sm text-stone-400">Ingen ansvarlig</span>}
        </div>
      </div>
      <div className="rounded-xl bg-[#FBFAF8] border border-[#F3F1EC] p-3 space-y-2.5 text-sm">
        <InfoRow icon={CalendarRange} label="Periode">{daDate(t.start_date)} – {daDate(t.expected_end_date)}</InfoRow>
        <InfoRow icon={Clock} label="Varighed">{t.duration_days} dage</InfoRow>
        <InfoRow icon={Building2} label="Ansvarlig">{t.responsible_name || "—"}</InfoRow>
      </div>
      {(t.is_delayed || overlap) && (
        <div className="space-y-1.5">
          {t.is_delayed && <p className="text-xs font-medium text-[#A83232] flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Forsinket med {t.delay_days} dage</p>}
          {overlap && <p className="text-xs text-[#806B61] flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 shrink-0" /> Flere opgaver i denne periode.</p>}
        </div>
      )}
      <div className="flex gap-2">
        <Link to={t.link} className="flex-1"><Button variant="outline" size="sm" className="w-full rounded-xl" data-testid={`tp-open-${t.id}`}><ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Åbn {t.kind === "sag" ? "sag" : "projekt"}</Button></Link>
        <Button size="sm" className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" data-testid={`tp-edit-${t.id}`} onClick={onEdit}><Pencil className="h-3.5 w-3.5 mr-1.5" /> Redigér</Button>
      </div>
    </div>
  );
}

function EditDialog({ task, users, onClose, onSaved }) {
  const initialResp = task.responsible_user_ids && task.responsible_user_ids.length ? task.responsible_user_ids : (task.responsible_user_id ? [task.responsible_user_id] : []);
  const [responsibles, setResponsibles] = useState(initialResp);
  const [status, setStatus] = useState(task.status);
  const [duration, setDuration] = useState(String(task.duration_days));
  const [saving, setSaving] = useState(false);
  const arrEq = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (!arrEq(responsibles, initialResp)) body.responsible_user_ids = responsibles;
      if (status !== task.status) body.status = status;
      if (Number(duration) !== task.duration_days) body.duration_days = Number(duration);
      if (Object.keys(body).length) {
        if (task.kind === "sag") await api.patch(`/cases/${task.case_id}/tasks/${task.task_id}`, body);
        else await api.patch(`/project-steps/${task.step_id}`, body);
      }
      toast.success("Opgaven er opdateret");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kunne ikke gemme");
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl" data-testid="tp-edit-dialog">
        <DialogHeader>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400">{task.source_label}{task.customer_name ? ` · ${task.customer_name}` : ""}</p>
          <DialogTitle className="font-light text-2xl leading-tight">{task.group_title}</DialogTitle>
          <DialogDescription className="text-sm text-stone-500">{task.item_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 mt-1">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Ansvarlige</Label>
            <ResponsibleMultiSelect users={users} contactName={null} value={responsibles} onChange={setResponsibles} testid="tp-edit-responsible" className="h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Status</Label>
            <div className="grid grid-cols-2 gap-2" data-testid="tp-edit-status">
              {TASK_STATUSES.map((s) => {
                const active = status === s;
                return (
                  <button key={s} type="button" onClick={() => setStatus(s)} data-testid={`tp-edit-status-${s}`}
                    className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium [transition-property:background-color,border-color,color] duration-200 ${active ? "border-[#A83232] bg-[#F1DEDA] text-[#A83232]" : "border-[#EBE8E3] bg-white text-stone-600 hover:border-stone-300"}`}>
                    <span className={`h-2.5 w-2.5 rounded-full ${DOT[s]}`} />
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Varighed</Label>
            <div className="relative">
              <Input type="number" min="0" value={duration} onChange={(e) => setDuration(e.target.value)} className="rounded-xl h-11 pr-12" data-testid="tp-edit-duration" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-stone-400 pointer-events-none">dage</span>
            </div>
          </div>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" className="rounded-xl" onClick={onClose} data-testid="tp-edit-cancel">Annullér</Button>
          <Button className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" onClick={save} disabled={saving} data-testid="tp-edit-save">{saving ? "Gemmer…" : "Gem"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
